import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { MatchResult, Lineup } from '@stanwars/sim';
import { PixelText } from '../ui/PixelText';
import { PixelButton } from '../ui/PixelButton';
import { PixelPanel } from '../ui/PixelPanel';
import { BattleScene } from '../battle/BattleScene';
import { api } from '../api/client';
import { content } from '../content/bundle';
import { usePlayerStore } from '../store/player';

function buildTutorialLineup(memberIds: string[], playerId: string): Lineup {
  const slots = memberIds.map((memberId, i) => {
    const member = content.getMember(memberId);
    if (!member) throw new Error(`Tutorial member not found: ${memberId}`);
    const skills = content.getSkillsForMember(memberId);
    const passive = skills.find(s => s.type === 'passive');
    const actives = skills.filter(s => s.type === 'active');
    const ult = skills.find(s => s.type === 'ultimate');
    if (!passive || actives.length < 2 || !ult)
      throw new Error(`Incomplete skills for tutorial member ${memberId}`);
    return {
      member: {
        id: member.id,
        stageName: member.stageName.parody,
        groupId: member.groupId,
        role: member.role,
        rarity: member.rarity,
        stats: member.stats
      },
      position: i as 0 | 1 | 2 | 3 | 4,
      loadout: {
        passiveId: passive.id,
        activeIds: [actives[0]!.id, actives[1]!.id] as [string, string],
        ultimateId: ult.id
      },
      skills: [passive, actives[0]!, actives[1]!, ult].map(s => ({
        id: s.id,
        ownerMemberId: s.ownerMemberId,
        type: s.type,
        archetypeId: s.archetypeId,
        params: s.params,
        cooldown: s.cooldown,
        flavorName: s.flavorName,
        flavorText: s.flavorText
      }))
    };
  });
  return { playerId, slots: slots as Lineup['slots'] };
}

const TUTORIAL_LINEUP_IDS = ['jungkook', 'karina', 'lisa', 'bangchan', 'minji'];

const ROLE_DESCRIPTIONS: Array<{ role: string; color: string; descKey: string }> = [
  { role: 'Rapper',   color: '#FF3AA6', descKey: 'High single-target damage. Counters Vocalist.' },
  { role: 'Vocalist', color: '#3AF0FF', descKey: 'Sustain + heals. Counters Dancer.' },
  { role: 'Dancer',   color: '#FFD23A', descKey: 'Speed-based AoE. Counters Visual.' },
  { role: 'Visual',   color: '#3AFF8E', descKey: 'Charming debuffs. Counters Maknae.' },
  { role: 'Maknae',   color: '#FF8E3A', descKey: 'Burst ultimate. Counters Rapper.' },
];

type Step = '1.1-intro' | '1.1-battle' | '1.2' | '1.3-loading' | '1.3-result';

export function Tutorial() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const me = usePlayerStore(s => s.me);
  const [step, setStep] = useState<Step>('1.1-intro');
  const [battleResult, setBattleResult] = useState<MatchResult | null>(null);
  const [lineupA, setLineupA] = useState<Lineup | null>(null);
  const [lineupB, setLineupB] = useState<Lineup | null>(null);
  const [pullResult, setPullResult] = useState<{ memberId: string; rarity: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Use the player's actual roster members (first 5 owned members).
  // Falls back to the hardcoded list only if the roster has fewer than 5 members somehow.
  const rosterMemberIds = (() => {
    if (!me) return TUTORIAL_LINEUP_IDS;
    const roster = (me.roster as Array<{ memberId: string }>) ?? [];
    const ids = roster.slice(0, 5).map(r => r.memberId);
    return ids.length >= 5 ? ids : TUTORIAL_LINEUP_IDS;
  })();

  // IDs to show in the intro sprite strip — use roster if available
  const displayLineupIds = rosterMemberIds.slice(0, 5);

  const startBattle = async () => {
    setError(null);
    try {
      const A = buildTutorialLineup(rosterMemberIds, 'tutorial-player');
      const memberIds = A.slots.map(s => s.member.id);
      const res = await api.match.campaign({
        stageId: '1-1',
        lineup: { memberIds, requestId: `tutorial-1-1-${crypto.randomUUID()}` },
      });
      const stage = content.getStage('1-1');
      const enemyIds = stage?.enemyLineup.map(e => e.memberId) ?? rosterMemberIds.slice().reverse();
      const B = buildTutorialLineup(enemyIds.slice(0, 5), 'tutorial-enemy');
      setBattleResult(res.result as MatchResult);
      setLineupA(A);
      setLineupB(B);
      setStep('1.1-battle');
    } catch (e) {
      setError(String(e));
    }
  };

  useEffect(() => {
    if (step !== '1.3-loading') return;
    let cancelled = false;
    void (async () => {
      // Try free pull first; if daily limit is exhausted, try ticket pull.
      // If both fail, synthesize a result from the first roster member the
      // player already owns — the tutorial just needs to show the gacha UX.
      const tryPull = async (useFree: boolean) => {
        return api.pull.post({ requestId: `tutorial-pull-${crypto.randomUUID()}`, useFree });
      };
      try {
        let res;
        try {
          res = await tryPull(true);
        } catch {
          try {
            res = await tryPull(false);
          } catch {
            // Both exhausted — use first roster member as a stand-in
            const first = rosterMemberIds[0];
            res = { memberId: first ?? 'suga', rarity: 'R', isDuplicate: true, shardsAwarded: 10 };
          }
        }
        if (!cancelled) {
          setPullResult({ memberId: res.memberId, rarity: res.rarity });
          setStep('1.3-result');
        }
      } catch (e) {
        if (!cancelled) setError(String(e));
      }
    })();
    return () => { cancelled = true; };
  }, [step, rosterMemberIds]);

  const handleFinish = async () => {
    try {
      await api.me.completeTutorial();
    } catch {
      // Non-fatal
    }
    nav('/');
  };

  /* ─── Step 1.1: Intro ─── */
  if (step === '1.1-intro') {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center gap-6 p-6 relative overflow-hidden">
        <img
          src="/stanwars/sprites/groups/bts.png"
          alt=""
          aria-hidden="true"
          className="pointer-events-none select-none"
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'fill', imageRendering: 'pixelated', opacity: 0.22 }}
        />
        <PixelText size="2xl" color="pink">{t('tutorial.title')}</PixelText>
        <PixelText size="lg" color="cyan">{t('tutorial.step_11_title')}</PixelText>

        <div className="flex gap-4 justify-center relative">
          {displayLineupIds.map(id => (
            <div
              key={id}
              style={{ width: 96, height: 96, overflow: 'hidden', imageRendering: 'pixelated' }}
            >
              <img
                src={`/stanwars/sprites/members/${id}/base.png`}
                alt={id}
                style={{ imageRendering: 'pixelated', width: 384, height: 384, maxWidth: 'none' }}
              />
            </div>
          ))}
        </div>

        <PixelPanel className="max-w-sm text-center relative">
          <PixelText size="sm" color="white">
            {t('tutorial.step_11_desc1')}
          </PixelText>
          <PixelText size="sm" color="white" className="block mt-2">
            {t('tutorial.step_11_desc2')}
          </PixelText>
        </PixelPanel>

        {error && (
          <PixelPanel className="max-w-sm w-full text-center relative border-[#ffd23a]">
            <PixelText size="sm" color="yellow">{error}</PixelText>
            <PixelButton
              variant="ghost"
              className="mt-3 w-full"
              onClick={() => { setError(null); void startBattle(); }}
            >
              {t('tutorial.retry')}
            </PixelButton>
          </PixelPanel>
        )}

        <PixelButton
          onClick={startBattle}
          className="relative px-8 py-4 text-base"
        >
          {t('tutorial.continue')}
        </PixelButton>
      </main>
    );
  }

  /* ─── Step 1.1: Battle ─── */
  if (step === '1.1-battle' && battleResult && lineupA && lineupB) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center bg-ink-950">
        <PixelText size="base" color="pink" className="mb-2">{t('tutorial.step_11_battle')}</PixelText>
        <BattleScene
          result={battleResult}
          lineupA={lineupA}
          lineupB={lineupB}
          onComplete={() => setStep('1.2')}
        />
      </main>
    );
  }

  /* ─── Step 1.2: Meet the roles ─── */
  if (step === '1.2') {
    return (
      <main className="min-h-screen flex flex-col items-center gap-6 p-6 relative overflow-hidden">
        <img
          src="/stanwars/sprites/groups/aespa.png"
          alt=""
          aria-hidden="true"
          className="pointer-events-none select-none"
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'fill', imageRendering: 'pixelated', opacity: 0.22 }}
        />
        <PixelText size="2xl" color="pink">{t('tutorial.title')}</PixelText>
        <PixelText size="lg" color="cyan">{t('tutorial.step_12_title')}</PixelText>
        <PixelText size="sm" color="white" className="opacity-70 relative">
          {t('tutorial.step_12_hint')}
        </PixelText>
        <div className="flex flex-col gap-3 w-full max-w-sm relative">
          {ROLE_DESCRIPTIONS.map(({ role, color, descKey }) => (
            <PixelPanel key={role} className="flex items-start gap-3">
              <span style={{ color, fontFamily: 'monospace', fontWeight: 'bold', minWidth: 80 }}>
                [{role}]
              </span>
              <PixelText size="sm" color="white">{descKey}</PixelText>
            </PixelPanel>
          ))}
        </div>
        <PixelButton
          onClick={() => setStep('1.3-loading')}
          className="relative px-8 py-4 text-base"
        >
          {t('tutorial.continue')}
        </PixelButton>
      </main>
    );
  }

  /* ─── Step 1.3: Loading ─── */
  if (step === '1.3-loading') {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center gap-6 p-6">
        <PixelText size="2xl" color="pink">{t('tutorial.title')}</PixelText>
        <PixelText size="lg" color="cyan">{t('tutorial.step_13_title')}</PixelText>
        <PixelText size="sm" color="white" className="opacity-70">
          {t('tutorial.step_13_pulling')}
        </PixelText>
        {error && <PixelText size="sm" color="yellow">{error}</PixelText>}
      </main>
    );
  }

  /* ─── Step 1.3: Pull result ─── */
  if (step === '1.3-result' && pullResult) {
    const rarityColors: Record<string, string> = {
      SSR: '#FFD23A', SR: '#FF3AA6', R: '#3AF0FF', N: '#FFFFFF'
    };
    const color = (rarityColors[pullResult.rarity] ?? '#FFFFFF') as 'pink' | 'cyan' | 'yellow' | 'white';
    const pulledMember = content.getMember(pullResult.memberId);
    const displayedName = pulledMember?.stageName.legal ?? pullResult.memberId;
    return (
      <main className="min-h-screen flex flex-col items-center justify-center gap-6 p-6">
        <PixelText size="2xl" color="pink">{t('tutorial.title')}</PixelText>
        <PixelText size="lg" color="cyan">{t('tutorial.step_13_title')}</PixelText>
        <PixelPanel className="text-center max-w-sm w-full">
          <PixelText size="sm" color="white" className="opacity-70 block mb-2">{t('tutorial.step_13_pull_result')}</PixelText>
          <PixelText size="xl" color={color}>{pullResult.rarity}</PixelText>
          <PixelText size="base" color="white" className="block mt-2">{displayedName}</PixelText>
        </PixelPanel>
        {error && <PixelText size="sm" color="yellow">{error}</PixelText>}
        <PixelButton onClick={handleFinish}>{t('tutorial.lets_play')}</PixelButton>
      </main>
    );
  }

  // Fallback
  return (
    <main className="min-h-screen flex flex-col items-center justify-center gap-6 p-6">
      <PixelText size="base" color="yellow">{t('tutorial.loading')}</PixelText>
    </main>
  );
}
